import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";
import { ArrowRight, ShieldCheck, Sparkles, Wrench, Users, Building2, Star, MapPin, Calculator, FileCheck2, Check, Clock, Headset, UserCheck, Activity } from "lucide-react";
import { LeadForm } from "@/components/common/LeadForm";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { SERVICE_ICONS, INDUSTRY_ICONS } from "@/data/static";

const ORION_PHOTOS = [
  { src: "https://customer-assets.emergentagent.com/job_orion-integrated-fm/artifacts/clknr0ja_WhatsApp%20Image%202026-06-20%20at%208.31.33%20PM%20%285%29.jpeg", label: "Deep-clean crew · JVC" },
  { src: "https://customer-assets.emergentagent.com/job_orion-integrated-fm/artifacts/csdgqx8a_WhatsApp%20Image%202026-06-20%20at%208.31.33%20PM%20%284%29.jpeg", label: "On-site · Business Bay" },
  { src: "https://customer-assets.emergentagent.com/job_orion-integrated-fm/artifacts/dsr2wxpg_WhatsApp%20Image%202026-06-20%20at%208.31.33%20PM%20%283%29.jpeg", label: "Warehouse FM · JAFZA" },
];

const ServiceCard = ({ slug, title, short }) => {
  const Icon = SERVICE_ICONS[slug] || Building2;
  return (
    <Link
      data-testid={`service-card-${slug}`}
      to={`/services/${slug}`}
      className="group relative bg-white border border-slate-200 rounded-2xl p-7 card-hover overflow-hidden"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
          <Icon className="w-6 h-6" />
        </div>
        <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-blue-700 group-hover:translate-x-1 rtl-flip transition-all" />
      </div>
      <h3 className="mt-6 font-heading text-xl font-medium text-slate-900">{title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{short}</p>
    </Link>
  );
};

const IndustryCard = ({ item }) => {
  const Icon = INDUSTRY_ICONS[item.slug] || Building2;
  return (
    <div
      data-testid={`industry-card-${item.slug}`}
      className="bg-white border border-slate-200 rounded-2xl p-7 card-hover"
    >
      <div className="w-11 h-11 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="mt-5 font-heading text-lg font-medium text-slate-900">{item.title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{item.desc}</p>
    </div>
  );
};

export default function Home() {
  const { t } = useLang();
  const services = ["fm", "cleaning", "maintenance", "manpower", "snagging", "projects"];
  const why = t("why.items");
  const industries = t("industries.items");
  const testimonials = t("testimonials.items");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: COMPANY.brandFull,
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c",
    "@id": "https://orion-fm.ae",
    url: "https://orion-fm.ae",
    telephone: COMPANY.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: "Ajman Freezone",
      addressLocality: "Ajman",
      addressCountry: "AE",
    },
    areaServed: ["Dubai", "Abu Dhabi", "Sharjah", "Ajman"],
  };

  return (
    <div data-testid="home-page">
      <SEO
        title={`${COMPANY.brandFull} — Integrated Facility Management UAE`}
        description="All-in-one facility management in UAE. Cleaning, maintenance, AMC and manpower under one SLA-driven partner. Serving Dubai, Abu Dhabi and Sharjah."
        jsonLd={jsonLd}
      />

      {/* HERO — dark cinematic split with real Orion proof photos */}
      <section className="relative overflow-hidden bg-[#050A15] text-white">
        {/* Ambient background: Dubai skyline is now the dominant visual */}
        <div className="absolute inset-0 pointer-events-none">
          <img
            src="https://images.unsplash.com/photo-1518684079-3c830dcef090?crop=entropy&cs=srgb&fm=jpg&q=90&w=2400"
            alt="Dubai skyline"
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover object-center opacity-85"
          />
          {/* Left-anchored gradient: dark where the H1 sits, clear on the right where the white form card is */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#050A15]/90 via-[#050A15]/55 to-[#050A15]/15" />
          {/* Bottom tint so stats + CTAs stay readable */}
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#050A15]/85 to-transparent" />
          {/* Softer color accents that don't wash out the skyline */}
          <div className="absolute -top-40 -right-32 w-[420px] h-[420px] rounded-full bg-blue-600/15 blur-[140px]" />
          <div className="absolute -bottom-40 -left-32 w-[360px] h-[360px] rounded-full bg-amber-500/10 blur-[120px]" />
        </div>

        <div className="container-orion relative pt-14 lg:pt-20 pb-16 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* LEFT — value prop + floating proof photos */}
            <motion.div
              className="lg:col-span-7"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
              }}
            >
              <motion.div
                variants={{ hidden: { opacity: 0, y: 14 }, visible: { opacity: 1, y: 0 } }}
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-300 border border-blue-400/30 rounded-full px-3 py-1.5"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                {t("hero.eyebrow")}
              </motion.div>

              <motion.h1
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                className="mt-6 font-heading text-4xl sm:text-5xl lg:text-[5rem] xl:text-[5.5rem] tracking-tighter font-medium leading-[1.02] text-white"
              >
                {t("hero.title")}
              </motion.h1>

              <motion.p
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                className="mt-6 text-base sm:text-lg text-slate-300/90 max-w-xl leading-relaxed"
              >
                {t("hero.subtitle")}
              </motion.p>

              <motion.div
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                className="mt-8 flex flex-wrap gap-3"
              >
                <Link
                  to="/health-check"
                  data-testid="hero-cta-quote"
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
                >
                  {t("hero.ctaPrimary")}
                  <ArrowRight className="w-4 h-4 rtl-flip" />
                </Link>
                <a
                  data-testid="hero-cta-whatsapp"
                  href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent("Hello Orion FM, I'd like to chat about facility management for my site.")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:bg-white/10 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
                >
                  {t("hero.ctaSecondary")}
                </a>
              </motion.div>

              {/* Floating proof photos — real Orion teams */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }}
                className="mt-12 flex flex-wrap gap-3"
              >
                {ORION_PHOTOS.map((p, i) => (
                  <motion.div
                    key={p.src}
                    data-testid={`hero-proof-${i}`}
                    animate={{ y: [0, -8, 0] }}
                    transition={{ repeat: Infinity, duration: 5 + i * 0.6, ease: "easeInOut", delay: i * 0.4 }}
                    className="relative w-[150px] sm:w-[180px] aspect-[3/4] rounded-2xl overflow-hidden border border-white/15 shadow-2xl shadow-black/40 group"
                  >
                    <img src={p.src} alt={p.label} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-3">
                      <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-white/90">
                        {p.label}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* Trust strip */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                className="mt-10 flex flex-wrap items-center gap-5 text-sm text-slate-300"
              >
                <div className="flex items-center gap-1.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                  <span className="ms-1 font-semibold text-white">4.8</span>
                  <span className="text-slate-400">· Google (9 reviews)</span>
                </div>
                <div className="hidden sm:block w-px h-4 bg-white/15" />
                <div className="inline-flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-300" />
                  Ajman Free Zone HQ · UAE-wide
                </div>
                <div className="hidden md:block w-px h-4 bg-white/15" />
                <div className="inline-flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-blue-300" />
                  DEWA · DM · Civil Defence
                </div>
              </motion.div>
            </motion.div>

            {/* RIGHT — solid white form card that pops from the dark hero */}
            <motion.div
              className="lg:col-span-5 lg:sticky lg:top-24"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <div className="relative">
                {/* Soft glow behind card */}
                <div className="absolute -inset-4 bg-blue-500/20 blur-3xl rounded-full pointer-events-none" />
                <div className="relative bg-white rounded-3xl p-7 sm:p-8 shadow-2xl shadow-black/40 border border-white/10">
                  <div className="text-eyebrow text-blue-700">{t("leadForm.heading")}</div>
                  <h2 className="mt-2 font-heading text-2xl font-medium text-slate-900 leading-tight">
                    {t("leadForm.sub")}
                  </h2>
                  <div className="mt-6">
                    <LeadForm pageSource="home-hero" simple />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Stats strip */}
          <div className="mt-16 lg:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
            {[
              { v: "<1hr", l: t("hero.stat1") },
              { v: "25+", l: t("hero.stat2") },
              { v: "50+", l: t("hero.stat3") },
              { v: "99.6%", l: t("hero.stat4") },
            ].map((s, i) => (
              <div key={i} className="bg-[#050A15] p-7 lg:p-9">
                <div className="font-heading text-3xl lg:text-5xl font-medium tracking-tighter text-blue-300">
                  {s.v}
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUSTED BY + ISO CERTIFICATIONS */}
      <section className="py-16 lg:py-20 bg-white border-y border-slate-200">
        <div className="container-orion">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-700 flex items-center gap-3">
                <span className="w-8 h-px bg-blue-700" />
                Trusted by
              </div>
              <div className="mt-6 grid grid-cols-3 sm:grid-cols-5 gap-3 sm:gap-4">
                {[
                  {
                    name: "ARTAR Real Estate",
                    sub: "Property developer",
                    logo: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/a8v8q457_image.png",
                    tone: "light",
                  },
                  {
                    name: "Kaizen Asset Management",
                    sub: "Real estate & investment",
                    logo: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/2ewf5kr2_image.png",
                    tone: "light",
                  },
                  {
                    name: "Hunter Foods",
                    sub: "F&B manufacturer",
                    logo: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/ao81rt1c_image.png",
                    tone: "light",
                  },
                  {
                    name: "Intero Services",
                    sub: "Professional services",
                    logo: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/banbbet4_image.png",
                    tone: "light",
                  },
                  {
                    name: "Les Toises Wellbeing",
                    sub: "Wellbeing center",
                    logo: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/5ne7zid7_image.png",
                    tone: "light",
                  },
                ].map((c) => (
                  <div
                    key={c.name}
                    data-testid={`client-logo-${c.name.split(" ")[0].toLowerCase()}`}
                    title={`${c.name} — ${c.sub}`}
                    className="group bg-white border border-slate-200 rounded-2xl h-24 sm:h-28 flex items-center justify-center px-3 hover:border-blue-300 hover:shadow-md hover:shadow-blue-900/5 transition-all"
                  >
                    <img
                      src={c.logo}
                      alt={c.name}
                      loading="lazy"
                      className="max-h-14 sm:max-h-16 w-auto object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                    />
                  </div>
                ))}
              </div>
              <div className="mt-4 text-xs text-slate-500">
                And a growing list of UAE developers, F&B, wellbeing and services operators.{" "}
                <Link to="/partners" data-testid="home-view-partners" className="font-semibold text-blue-700 hover:underline">
                  View all partners →
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-700 flex items-center gap-3">
                <ShieldCheck className="w-3.5 h-3.5" />
                Certified to international standards
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { code: "ISO 9001:2015", label: "Quality Management" },
                  { code: "ISO 14001", label: "Environmental Management" },
                  { code: "ISO 45001", label: "Occupational Health & Safety" },
                ].map((iso) => (
                  <div
                    key={iso.code}
                    data-testid={`iso-badge-${iso.code.split(":")[0].replace(/\s+/g, "-").toLowerCase()}`}
                    className="bg-slate-950 text-white rounded-2xl p-5 border border-slate-800"
                  >
                    <div className="w-9 h-9 rounded-lg bg-blue-500/15 text-blue-300 flex items-center justify-center">
                      <ShieldCheck className="w-4 h-4" />
                    </div>
                    <div className="mt-4 font-heading text-sm font-semibold text-white leading-tight">
                      {iso.code}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-400 leading-snug">
                      {iso.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FREE AUDIT BANNER */}
      <section className="bg-slate-950 text-white py-20 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: "radial-gradient(circle at 20% 50%, #2563EB 0%, transparent 50%)",
        }} />
        <div className="container-orion relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-300 border border-blue-400/30 rounded-full px-3 py-1.5">
              <Sparkles className="w-3 h-3" />
              {t("audit.eyebrow")}
            </div>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05]">
              {t("audit.title")}
            </h2>
            <p className="mt-5 text-slate-300 text-lg leading-relaxed max-w-xl">
              {t("audit.desc")}
            </p>
            <Link
              to="/health-check"
              data-testid="home-audit-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
            >
              {t("audit.cta")}
              <ArrowRight className="w-4 h-4 rtl-flip" />
            </Link>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-7 backdrop-blur-sm">
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-300">
                {t("audit.inspectLabel")}
              </div>
              <ul className="mt-5 space-y-3">
                {t("audit.items").map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-200">
                    <Check className="w-4 h-4 text-blue-400 mt-1 shrink-0" />
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES — Dark Control Room bento */}
      <section className="py-24 lg:py-32 bg-[#050A15] text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full bg-blue-600/10 blur-[160px]" />
        </div>
        <div className="container-orion relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-300 border border-blue-400/30 rounded-full px-3 py-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                {t("services.sectionLabel")}
              </div>
              <h2 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-white leading-tight">
                {t("services.sectionTitle")}
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 self-end">
              <p className="text-slate-300/85 text-lg leading-relaxed">{t("services.sectionDesc")}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {services.map((k) => {
              const slug = t(`services.${k}.slug`);
              const Icon = SERVICE_ICONS[slug] || Building2;
              return (
                <Link
                  key={k}
                  to={`/services/${slug}`}
                  data-testid={`service-card-${slug}`}
                  className="group relative bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-blue-400/40 rounded-2xl p-7 transition-all overflow-hidden"
                >
                  <div className="absolute -top-16 -right-16 w-32 h-32 rounded-full bg-blue-500/0 group-hover:bg-blue-500/20 blur-2xl transition-all duration-500" />
                  <div className="relative flex items-start justify-between gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-300 flex items-center justify-center group-hover:bg-blue-500 group-hover:text-white transition-colors">
                      <Icon className="w-6 h-6" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-600 group-hover:text-blue-300 group-hover:translate-x-1 rtl-flip transition-all" />
                  </div>
                  <h3 className="relative mt-6 font-heading text-xl font-medium text-white">
                    {t(`services.${k}.title`)}
                  </h3>
                  <p className="relative mt-2 text-sm text-slate-400 leading-relaxed">
                    {t(`services.${k}.short`)}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR WORK — Animated marquee of real Orion sites */}
      <section className="py-20 lg:py-24 bg-white overflow-hidden">
        <div className="container-orion">
          <div className="max-w-3xl mb-10 flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="text-eyebrow">Our work · Across the UAE</div>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-[1.05]">
                Real work, real teams.
              </h2>
              <p className="mt-4 text-base text-slate-600 max-w-xl leading-relaxed">
                Uniformed crews, branded PPE and heavy equipment on live sites. This is the standard your facility gets — day one.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
              Live from the field
            </div>
          </div>
        </div>

        {(() => {
          const marqueeItems = [
            {
              src: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/3f1bd7ca5cbb9865_UP%202.jpeg",
              tag: "Office AMC",
              label: "Unique Properties · Office lobby",
              meta: "Dubai · Live AMC",
            },
            {
              src: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/uj274oin_WhatsApp%20Image%202026-09-05%20at%208.54.08%20PM.jpeg",
              tag: "Project Management",
              label: "Orion crew · Site handover",
              meta: "Ajman · Q3 2026",
            },
            {
              src: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/ro6meixl_WhatsApp%20Image%202026-09-05%20at%208.51.12%20PM%20%281%29.jpeg",
              tag: "Pool Renovation",
              label: "Pool refurbishment crew",
              meta: "Ajman FZ · Aug 2026",
            },
            {
              src: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/lkbu36gp_WhatsApp%20Image%202026-08-26%20at%2010.11.25%20AM.jpeg",
              tag: "Landscaping · Heavy Equipment",
              label: "CAT 216B3 skid-steer",
              meta: "Ajman FZ · Aug 2026",
            },
            {
              src: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/md7xadxo_WhatsApp%20Image%202026-04-22%20at%2012.26.03%20PM%20%284%29.jpeg",
              tag: "Community FM",
              label: "Residential towers",
              meta: "Dubai · Live AMC",
            },
            {
              src: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/a1ifbilq_WhatsApp%20Image%202026-04-22%20at%2012.26.03%20PM%20%283%29.jpeg",
              tag: "Deep Cleaning",
              label: "Bathroom finish · handover",
              meta: "Business Bay · 2026",
            },
            {
              src: "https://customer-assets.emergentagent.com/job_orion-integrated-fm/artifacts/csdgqx8a_WhatsApp%20Image%202026-06-20%20at%208.31.33%20PM%20%284%29.jpeg",
              tag: "On-site Ops",
              label: "Orion team dispatch",
              meta: "JVC · 2026",
            },
            {
              src: "https://customer-assets.emergentagent.com/job_orion-integrated-fm/artifacts/dsr2wxpg_WhatsApp%20Image%202026-06-20%20at%208.31.33%20PM%20%283%29.jpeg",
              tag: "Warehouse FM",
              label: "Industrial deep clean",
              meta: "JAFZA · 2026",
            },
            {
              src: "https://customer-assets.emergentagent.com/job_orion-integrated-fm/artifacts/xvtqga3y_WhatsApp%20Image%202026-06-20%20at%208.31.33%20PM%20%281%29.jpeg",
              tag: "Post-construction",
              label: "Handover cleaning",
              meta: "Sharjah · 2026",
            },
          ];
          // Duplicate for seamless infinite loop
          const track = [...marqueeItems, ...marqueeItems];
          return (
            <div
              className="relative"
              data-testid="home-our-work-marquee"
            >
              {/* Fade edges */}
              <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-32 bg-gradient-to-r from-white to-transparent z-10" />
              <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-32 bg-gradient-to-l from-white to-transparent z-10" />

              <div className="marquee-track flex gap-5 w-max">
                {track.map((img, i) => (
                  <div
                    key={i}
                    data-testid={`marquee-card-${i % marqueeItems.length}`}
                    className="relative w-[280px] sm:w-[340px] lg:w-[380px] aspect-[4/5] rounded-3xl overflow-hidden shrink-0 group bg-slate-100 border border-slate-200"
                  >
                    <img
                      src={img.src}
                      alt={img.label}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.06]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/25 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-white bg-blue-600/90 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-lg shadow-blue-900/30">
                        {img.tag}
                      </span>
                    </div>
                    <div className="absolute bottom-5 left-5 right-5 text-white">
                      <div className="font-heading text-base sm:text-lg font-medium leading-tight">
                        {img.label}
                      </div>
                      <div className="mt-1.5 text-[11px] uppercase tracking-[0.15em] text-blue-200/90 font-semibold">
                        {img.meta}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </section>

      {/* TRUST / HOW WE DELIVER */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container-orion">
          <div className="max-w-3xl">
            <div className="text-eyebrow">{t("trust.sectionLabel")}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              {t("trust.sectionTitle")}
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {t("trust.items").map((it, i) => {
              const Icon = [Headset, Clock, UserCheck, Activity][i] || Headset;
              return (
                <div
                  key={i}
                  data-testid={`trust-item-${i}`}
                  className="bg-slate-50 border border-slate-200 rounded-2xl p-7 card-hover"
                >
                  <div className="w-11 h-11 rounded-lg bg-blue-600 text-white flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-medium text-slate-900">{it.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container-orion">
          <div className="max-w-3xl">
            <div className="text-eyebrow">{t("why.sectionLabel")}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              {t("why.sectionTitle")}
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden">
            {why.map((item, i) => (
              <div key={i} className="bg-white p-8 lg:p-10 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-2 text-blue-700">
                  <ShieldCheck className="w-5 h-5" />
                  <span className="text-xs uppercase tracking-[0.18em] font-semibold">0{i + 1}</span>
                </div>
                <h3 className="mt-5 font-heading text-xl font-medium text-slate-900 leading-snug">
                  {item.title}
                </h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TOOLS / LEAD MAGNETS */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container-orion">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <div className="text-eyebrow">{t("tools.sectionLabel")}</div>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
                {t("tools.sectionTitle")}
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 self-end">
              <p className="text-slate-600 text-lg leading-relaxed">{t("tools.sectionDesc")}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Link
              data-testid="home-tool-calculator"
              to="/cost-calculator"
              className="beam-card group relative bg-slate-950 text-white rounded-3xl p-8 lg:p-10 overflow-hidden card-hover"
            >
              <div className="absolute inset-0 grid-bg opacity-10" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
                  <Calculator className="w-6 h-6" />
                </div>
                <h3 className="mt-7 font-heading text-2xl lg:text-3xl tracking-tight font-medium">
                  {t("tools.calc.title")}
                </h3>
                <p className="mt-3 text-slate-400 leading-relaxed">{t("tools.calc.short")}</p>
                <div className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-300 group-hover:gap-3 transition-all">
                  {t("tools.calc.cta")}
                  <ArrowRight className="w-4 h-4 rtl-flip" />
                </div>
              </div>
            </Link>
            <Link
              data-testid="home-tool-health"
              to="/health-check"
              className="beam-card beam-card-light group relative bg-white border border-slate-200 rounded-3xl p-8 lg:p-10 card-hover"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
                <FileCheck2 className="w-6 h-6" />
              </div>
              <h3 className="mt-7 font-heading text-2xl lg:text-3xl tracking-tight font-medium text-slate-900">
                {t("tools.health.title")}
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{t("tools.health.short")}</p>
              <div className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 group-hover:gap-3 transition-all">
                {t("tools.health.downloadBtn")}
                <ArrowRight className="w-4 h-4 rtl-flip" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* INDUSTRIES — Stark white break section */}
      <section className="py-32 lg:py-40 bg-white relative">
        <div className="container-orion">
          <div className="max-w-4xl">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-700 flex items-center gap-3">
              <span className="w-8 h-px bg-blue-700" />
              {t("industries.sectionLabel")}
            </div>
            <h2 className="mt-8 font-heading text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-medium text-slate-900 leading-[1.02]">
              {t("industries.sectionTitle")}
            </h2>
          </div>
          <div className="mt-16 lg:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-3xl overflow-hidden">
            {industries.map((item) => {
              const Icon = INDUSTRY_ICONS[item.slug] || Building2;
              return (
                <div
                  key={item.slug}
                  data-testid={`industry-card-${item.slug}`}
                  className="bg-white p-8 lg:p-10 group hover:bg-slate-50 transition-colors"
                >
                  <div className="w-11 h-11 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-6 font-heading text-xl font-medium text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-12">
            <Link to="/industries" data-testid="home-industries-cta" className="btn-ghost-orion">
              {t("common.viewAll")} <ArrowRight className="w-4 h-4 rtl-flip" />
            </Link>
          </div>
        </div>
      </section>

      {/* CASE STUDY / RESULTS */}
      <section className="py-24 lg:py-32 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
            alt="Warehouse logistics UAE"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/40" />
        </div>
        <div className="container-orion relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="text-eyebrow text-blue-400">Case Study · 3PL Logistics, Jebel Ali</div>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-white leading-tight">
                22% OPEX reduction. 99.7% uptime. 7 vendors → 1.
              </h2>
              <p className="mt-5 text-slate-400 max-w-xl leading-relaxed">
                A leading 3PL operator in Jebel Ali consolidated cleaning, MEP, fire, pest and
                manpower onto a single Orion AMC. Within 12 months OPEX dropped 22%, audit findings
                halved, and operations gained one accountable partner.
              </p>
            </div>
            <div className="lg:col-span-5 grid grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
              {[
                { v: "22%", l: "OPEX cut" },
                { v: "99.7%", l: "Uptime" },
                { v: "1", l: "Vendor" },
              ].map((s, i) => (
                <div key={i} className="bg-slate-950 p-6 lg:p-8 text-center">
                  <div className="font-heading text-3xl lg:text-4xl font-medium tracking-tighter text-blue-400">
                    {s.v}
                  </div>
                  <div className="mt-2 text-[10px] sm:text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container-orion">
          <div className="max-w-3xl mb-14">
            <div className="text-eyebrow">{t("testimonials.sectionLabel")}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              {t("testimonials.sectionTitle")}
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {testimonials.map((tt, i) => (
              <div
                key={i}
                className="bg-slate-50 border border-slate-200 rounded-2xl p-8 flex flex-col"
              >
                <div className="flex gap-1 text-amber-500">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="mt-5 text-base text-slate-800 leading-relaxed flex-1">"{tt.quote}"</p>
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <div className="font-heading text-base font-medium text-slate-900">
                    {tt.author}
                  </div>
                  <div className="text-sm text-slate-500">{tt.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
